// What an author is told of an announcement request that was refused, in words: the API's codes are matched here and never shown.

const own = (texts, key) => Boolean(texts) && Object.prototype.hasOwnProperty.call(texts, key)

// The text `texts` give the refusal `code`, otherwise `fallback`. A code that names the field at fault (`INVALID_ANNOUNCEMENT:title`) takes
// its field's text, or else its kind's (`INVALID_ANNOUNCEMENT:*`).
export const announcementRefusalText = (code, texts, fallback) => {
  if (typeof code !== 'string') return fallback
  if (own(texts, code)) return texts[code]
  const kind = code.includes(':') && `${code.slice(0, code.indexOf(':'))}:*`
  return kind && own(texts, kind) ? texts[kind] : fallback
}

// A request the gateway refused (HTTP 4xx) before anything reached Events, whatever the operation; an operation's own `refusals` win over
// these. Any other refusal, a validation text included, reads REQUEST_REFUSED.
export const REQUEST_REFUSALS = {
  SESSION_EXPIRED: 'Your session has expired. Sign in again, then repeat the change.',
  FORBIDDEN: 'Your account cannot change announcements.',
}
export const REQUEST_REFUSED = 'The request was rejected. Review your changes and try again.'

const TITLE_REFUSED = 'Use at most 1,000 characters for the title.'
const BODY_REFUSED = 'The announcement body is too large.'
const LANGUAGE_REFUSED = 'This language cannot be chosen for this text.'
const CONTENT_REFUSED = 'Fill in every field the original has.'
// Refused for the announcement, its event or its author, whatever the request. EVENT_NOT_FOUND: the event was deleted (or is missing), as
// the CP's other editors word it (Competitions/competitionOperation.js).
const ANNOUNCEMENT_REFUSALS = {
  ANNOUNCEMENT_NOT_FOUND: 'This announcement no longer exists.',
  EVENT_NOT_FOUND: 'This event is no longer available.',
  ERROR_PERMISSIONS: 'You do not have permission to change this announcement.',
}
// A save refused as written, by the field at fault (sportya-api common/eventEditing announcementTextsSchema).
const SAVE_REFUSALS = {
  ...ANNOUNCEMENT_REFUSALS,
  'INVALID_ANNOUNCEMENT:title': TITLE_REFUSED,
  'INVALID_ANNOUNCEMENT:message': BODY_REFUSED,
  'INVALID_ANNOUNCEMENT:originalLanguage': 'Choose Romanian, English or Bulgarian as the language the announcement is written in.',
  'INVALID_ANNOUNCEMENT:*': 'The announcement was refused as written. Review the title, the body and its language.',
}

// The refusals of an announcement's creation and of its edit (Events, on the socket).
export const CREATE_REFUSALS = {
  ...SAVE_REFUSALS,
  SOMETHING_WENT_WRONG: 'The announcement was not created: the server could not save it. Try again.',
}
export const EDIT_REFUSALS = {
  ...SAVE_REFUSALS,
  SOMETHING_WENT_WRONG: 'Your changes were not saved: the server could not save them. Try again.',
}

// The refusals of a command from the announcement's actions menu (Events, on the socket), by the action the author chose.
export const ACTION_REFUSALS = {
  pin: { ...ANNOUNCEMENT_REFUSALS, SOMETHING_WENT_WRONG: 'The announcement was not pinned: the server could not save the change. Try again.' },
  unpin: { ...ANNOUNCEMENT_REFUSALS, SOMETHING_WENT_WRONG: 'The announcement was not unpinned: the server could not save the change. Try again.' },
  enable: { ...ANNOUNCEMENT_REFUSALS, SOMETHING_WENT_WRONG: 'The announcement was not enabled: the server could not save the change. Try again.' },
  disable: { ...ANNOUNCEMENT_REFUSALS, SOMETHING_WENT_WRONG: 'The announcement was not disabled: the server could not save the change. Try again.' },
  remove: { ...ANNOUNCEMENT_REFUSALS, SOMETHING_WENT_WRONG: 'The announcement was not removed: the server could not save the change. Try again.' },
}

// The refusals of an author's command on the announcement's text block (Events, on the socket), a language confirmed or a translation
// written by hand: by the field at fault (sportya-api common/eventEditing announcementTextCommand), or by the block's state.
export const TEXT_REFUSALS = {
  ...ANNOUNCEMENT_REFUSALS,
  'INVALID_ANNOUNCEMENT_TEXT:content.title': TITLE_REFUSED,
  'INVALID_ANNOUNCEMENT_TEXT:content.message': BODY_REFUSED,
  'INVALID_ANNOUNCEMENT_TEXT:language': LANGUAGE_REFUSED,
  'INVALID_ANNOUNCEMENT_TEXT:*': CONTENT_REFUSED,
  INVALID_TRANSLATION_STATE: 'The translation changed meanwhile. Reload the announcement and try again.',
  INVALID_TRANSLATION_LANGUAGE: LANGUAGE_REFUSED,
  INVALID_TRANSLATION_CONTENT: CONTENT_REFUSED,
  SOMETHING_WENT_WRONG: 'The translation was not changed: the server could not save it. Try again.',
}

// Why the notifications of a saved announcement were not queued, as their receipt names it (Events notificationService.js); each reason
// follows "Notifications were not queued: ".
export const NOTIFICATION_REFUSALS = {
  ANNOUNCEMENT_CHANGED: 'the announcement changed after this save. Open it again, review it and notify players again.',
  ANNOUNCEMENT_DISABLED: 'the announcement is disabled. Enable it from the list, then notify players again.',
  ANNOUNCEMENT_NOT_FOUND: 'the announcement was removed.',
  EVENT_NOT_FOUND: 'the event is disabled or was deleted.',
  INVALID_NOTIFICATION_SELECTION: 'a selected competition is disabled or deleted. Select the competitions again.',
  INVALID_ANNOUNCEMENT_CONTENT: 'the announcement body has no text or image to send. Add a message, then notify players again.',
  NOTIFICATIONS_NOT_SENT: 'the notifications could not be sent in time. Notify players again.',
}
export const notificationRefusalText = code => 'Announcement saved. Notifications were not queued: '
  + announcementRefusalText(code, NOTIFICATION_REFUSALS, 'Review the announcement and competition selection.')
